import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import type { Booking, Therapist } from '../../types';

interface CalendarTabProps {
  bookings: Booking[];
  therapists: Therapist[];
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  calendarMonth: Date;
  setCalendarMonth: (date: Date) => void;
  getDatesWithBookings: Date[];
  selectedDateBookings: Booking[];
  getStatusBadge: (status: string) => React.ReactNode;
}

const HOURS = Array.from({ length: 14 }, (_, i) => i + 8); // 8:00 - 21:00
const HOUR_HEIGHT = 56; // px per hour row

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-200 border-amber-400 text-amber-900',
  assigned: 'bg-sky-200 border-sky-400 text-sky-900',
  accepted: 'bg-violet-200 border-violet-400 text-violet-900',
  completed: 'bg-emerald-200 border-emerald-400 text-emerald-900',
  cancelled: 'bg-red-200 border-red-400 text-red-500 line-through',
};

function getWeekDays(date: Date): Date[] {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday start
  const monday = new Date(d.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const dd = new Date(monday);
    dd.setDate(monday.getDate() + i);
    return dd;
  });
}

function toDateStr(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseBookingTime(booking: Booking): { hour: number; minute: number } | null {
  const dt = booking.booking_datetime;
  if (!dt) return null;
  const timePart = dt.includes('T') ? dt.split('T')[1] : null;
  if (!timePart) return null;
  const [h, m] = timePart.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  return { hour: h, minute: m };
}

export const CalendarTab = ({
  bookings, therapists, selectedDate, setSelectedDate,
  calendarMonth, setCalendarMonth, getDatesWithBookings,
  selectedDateBookings, getStatusBadge,
}: CalendarTabProps) => {
  const { t } = useLanguage();
  const [view, setView] = useState<'week' | 'day'>('week');

  const weekDays = useMemo(() => getWeekDays(selectedDate), [selectedDate]);
  const weekLabel = `${weekDays[0].toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${weekDays[6].toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;

  const activeTherapists = useMemo(() => therapists.filter(t => t.is_active !== false), [therapists]);

  // Map bookings by date+therapist for fast lookup
  const bookingMap = useMemo(() => {
    const map: Record<string, Booking[]> = {};
    bookings.forEach(b => {
      if (!b.booking_datetime || !b.assigned_therapist_id) return;
      const dateStr = b.booking_datetime.split('T')[0];
      const key = `${dateStr}_${b.assigned_therapist_id}`;
      if (!map[key]) map[key] = [];
      map[key].push(b);
    });
    return map;
  }, [bookings]);

  // Day bookings by therapist (for day view)
  const dayStr = toDateStr(selectedDate);
  const dayBookingsByTherapist = useMemo(() => {
    const map: Record<string, Booking[]> = {};
    activeTherapists.forEach(th => {
      map[th.id] = bookingMap[`${dayStr}_${th.id}`] || [];
    });
    return map;
  }, [dayStr, activeTherapists, bookingMap]);

  const navigateWeek = (dir: number) => {
    const nd = new Date(selectedDate);
    nd.setDate(nd.getDate() + dir * 7);
    setSelectedDate(nd);
    setCalendarMonth(nd);
  };

  const navigateDay = (dir: number) => {
    const nd = new Date(selectedDate);
    nd.setDate(nd.getDate() + dir);
    setSelectedDate(nd);
    setCalendarMonth(nd);
  };

  const goToToday = () => {
    const today = new Date();
    setSelectedDate(today);
    setCalendarMonth(today);
  };

  const isToday = (d: Date) => d.toDateString() === new Date().toDateString();
  const isSelected = (d: Date) => d.toDateString() === selectedDate.toDateString();

  return (
    <div className="space-y-4" data-testid="schedule-calendar">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToToday} data-testid="today-btn">Today</Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => view === 'week' ? navigateWeek(-1) : navigateDay(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => view === 'week' ? navigateWeek(1) : navigateDay(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <span className="font-semibold text-sm sm:text-base" data-testid="schedule-date-label">
            {view === 'week' ? weekLabel : selectedDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={view === 'week' ? 'default' : 'outline'} size="sm" onClick={() => setView('week')} data-testid="view-week-btn">Week</Button>
          <Button variant={view === 'day' ? 'default' : 'outline'} size="sm" onClick={() => setView('day')} data-testid="view-day-btn">Day</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        {/* Mini Calendar */}
        <Card className="xl:col-span-1">
          <CardContent className="pt-4 px-2">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              modifiers={{ hasBookings: getDatesWithBookings }}
              modifiersStyles={{ hasBookings: { backgroundColor: '#fce7f3', fontWeight: 'bold' } }}
              className="rounded-md border"
            />
            <p className="text-xs text-gray-500 mt-2 text-center">{t('admin.pinkDatesHaveBookings')}</p>
            {/* Legend */}
            <div className="mt-3 space-y-1 px-2">
              <p className="text-xs font-medium text-gray-600 mb-1">Status</p>
              {Object.entries(STATUS_COLORS).map(([status, cls]) => (
                <div key={status} className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-sm border ${cls.split(' ').slice(0, 2).join(' ')}`} />
                  <span className="text-xs capitalize text-gray-600">{status}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Schedule Grid */}
        <Card className="xl:col-span-3 overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5" />
              {view === 'week' ? 'Weekly Schedule' : `${selectedDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}`}
            </CardTitle>
            <CardDescription>
              {activeTherapists.length} therapist{activeTherapists.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {view === 'week' ? (
              <WeekView
                weekDays={weekDays}
                therapists={activeTherapists}
                bookingMap={bookingMap}
                isToday={isToday}
                isSelected={isSelected}
                onSelectDay={(d) => { setSelectedDate(d); setView('day'); }}
              />
            ) : (
              <DayView
                date={selectedDate}
                therapists={activeTherapists}
                bookingsByTherapist={dayBookingsByTherapist}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

/* ─── Week View ─── */
function WeekView({ weekDays, therapists, bookingMap, isToday, isSelected, onSelectDay }: {
  weekDays: Date[];
  therapists: Therapist[];
  bookingMap: Record<string, Booking[]>;
  isToday: (d: Date) => boolean;
  isSelected: (d: Date) => boolean;
  onSelectDay: (d: Date) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse min-w-[640px]">
        <thead>
          <tr>
            <th className="p-2 border-b border-r bg-gray-50 text-left text-xs font-medium text-gray-500 w-24 sticky left-0 z-10">Therapist</th>
            {weekDays.map((d) => (
              <th
                key={d.toISOString()}
                className={`p-2 border-b text-center text-xs font-medium cursor-pointer transition-colors hover:bg-pink-50 ${
                  isToday(d) ? 'bg-pink-100 text-pink-800' : isSelected(d) ? 'bg-blue-50 text-blue-700' : 'text-gray-600'
                }`}
                onClick={() => onSelectDay(d)}
                data-testid={`week-day-${toDateStr(d)}`}
              >
                <div>{d.toLocaleDateString('en-GB', { weekday: 'short' })}</div>
                <div className="text-base font-semibold">{d.getDate()}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {therapists.map((th) => (
            <tr key={th.id}>
              <td className="p-2 border-b border-r bg-gray-50 sticky left-0 z-10">
                <div className="flex items-center gap-2">
                  <img src={th.photo_url} alt={th.name} className="w-7 h-7 rounded-full object-cover" />
                  <div>
                    <div className="font-medium text-xs">{th.name}</div>
                    <div className={`text-[10px] ${th.availability_status === 'available' ? 'text-green-600' : 'text-gray-400'}`}>
                      {th.availability_status === 'available' ? 'Available' : 'Off'}
                    </div>
                  </div>
                </div>
              </td>
              {weekDays.map((d) => {
                const ds = toDateStr(d);
                const dayBookings = bookingMap[`${ds}_${th.id}`] || [];
                return (
                  <td
                    key={ds}
                    className={`p-1 border-b align-top cursor-pointer hover:bg-gray-50 transition-colors ${isToday(d) ? 'bg-pink-50/40' : ''}`}
                    onClick={() => onSelectDay(d)}
                    data-testid={`cell-${th.name}-${ds}`}
                  >
                    {dayBookings.length === 0 ? (
                      <span className="text-[10px] text-gray-300 block text-center">-</span>
                    ) : (
                      <div className="space-y-1">
                        {dayBookings.slice(0, 3).map((b) => {
                          const t = parseBookingTime(b);
                          const colorCls = STATUS_COLORS[b.status] || 'bg-gray-100 border-gray-300';
                          return (
                            <div
                              key={b.id}
                              className={`rounded px-1.5 py-0.5 text-[10px] leading-tight border ${colorCls} truncate`}
                              title={`${b.name} - ${b.service_type} (${b.duration_minutes}min)`}
                            >
                              {t ? `${String(t.hour).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}` : ''} {b.service_type.split(' ')[0]}
                            </div>
                          );
                        })}
                        {dayBookings.length > 3 && (
                          <div className="text-[10px] text-gray-500 text-center">+{dayBookings.length - 3} more</div>
                        )}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Day View (Timeline) ─── */
function DayView({ date, therapists, bookingsByTherapist }: {
  date: Date;
  therapists: Therapist[];
  bookingsByTherapist: Record<string, Booking[]>;
}) {
  const totalBookings = Object.values(bookingsByTherapist).reduce((s, arr) => s + arr.length, 0);

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[500px]">
        {/* Time header */}
        <div className="flex border-b">
          <div className="w-28 shrink-0 p-2 bg-gray-50 border-r text-xs font-medium text-gray-500">
            {totalBookings} booking{totalBookings !== 1 ? 's' : ''}
          </div>
          <div className="flex-1 relative" style={{ height: 32 }}>
            <div className="flex h-full">
              {HOURS.map((h) => (
                <div key={h} className="flex-1 border-r text-center text-[10px] text-gray-500 leading-[32px]">
                  {String(h).padStart(2, '0')}:00
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Therapist rows */}
        {therapists.map((th) => {
          const bks = bookingsByTherapist[th.id] || [];
          return (
            <div key={th.id} className="flex border-b last:border-b-0" data-testid={`day-row-${th.name}`}>
              <div className="w-28 shrink-0 p-2 bg-gray-50 border-r flex items-center gap-2">
                <img src={th.photo_url} alt={th.name} className="w-7 h-7 rounded-full object-cover" />
                <div>
                  <div className="font-medium text-xs">{th.name}</div>
                  <div className="text-[10px] text-gray-400">
                    {th.working_hours_start || '09:00'}-{th.working_hours_end || '21:00'}
                  </div>
                </div>
              </div>
              <div className="flex-1 relative" style={{ height: HOUR_HEIGHT }}>
                {/* Hour grid lines */}
                <div className="absolute inset-0 flex">
                  {HOURS.map((h) => (
                    <div key={h} className="flex-1 border-r border-dashed border-gray-100" />
                  ))}
                </div>
                {/* Booking blocks */}
                {bks.map((b) => {
                  const time = parseBookingTime(b);
                  if (!time) return null;
                  const startOffset = (time.hour - HOURS[0]) + time.minute / 60;
                  const durationHours = (b.duration_minutes || 60) / 60;
                  const leftPct = (startOffset / HOURS.length) * 100;
                  const widthPct = (durationHours / HOURS.length) * 100;
                  if (leftPct < 0 || leftPct >= 100) return null;
                  const colorCls = STATUS_COLORS[b.status] || 'bg-gray-100 border-gray-300';
                  return (
                    <div
                      key={b.id}
                      className={`absolute top-1 bottom-1 rounded border ${colorCls} flex items-center px-1.5 overflow-hidden`}
                      style={{ left: `${leftPct}%`, width: `${Math.min(widthPct, 100 - leftPct)}%` }}
                      title={`${b.name}\n${b.service_type} (${b.duration_minutes}min)\n${b.hotel_name}, Room ${b.room_number}\nStatus: ${b.status}`}
                      data-testid={`booking-block-${b.id}`}
                    >
                      <div className="truncate text-[11px] leading-tight">
                        <span className="font-medium">{String(time.hour).padStart(2, '0')}:{String(time.minute).padStart(2, '0')}</span>
                        {' '}{b.name} - {b.service_type.split(' ')[0]}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {therapists.length === 0 && (
          <div className="p-8 text-center text-gray-500">No active therapists</div>
        )}
      </div>
    </div>
  );
}
